import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, ArrowRight, Phone } from 'lucide-react';
import Header from './Header';
import Footer from './Footer';
import ContactForm from './ContactForm';

const ContactPage = ({ isMenuOpen, setIsMenuOpen }) => {
  const contactMethods = [
    {
      icon: Phone,
      title: 'Phone',
      description: 'Call anytime, we\'re available',
      link: 'tel:+421952175301',
      linkText: '+421 952 175 301'
    },
    {
      icon: Mail,
      title: 'Email',
      description: 'Write anytime, we\'ll get back to you',
      link: 'mailto:administrator@tuke.win',
      linkText: 'administrator@tuke.win'
    }
  ];

  return (
    <div className="min-h-screen bg-slate-900 text-white overflow-x-hidden">
      <Helmet>
        <title>Contact | TukeWIN</title>
        <meta name="description" content="Contact TukeWIN for web development, software protection and IT services. Call or write anytime." />
        <meta name="keywords" content="contact, TukeWIN, web development, IT services, software protection" />
      </Helmet>

      <Header isMenuOpen={isMenuOpen} setIsMenuOpen={setIsMenuOpen} />

      {/* Hero Section */}
      <section className="hero-bottom-fade section-glow-bottom relative min-h-[50vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900">
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,rgba(99,102,241,0.15),transparent_50%)]" />
          <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,0.1),transparent_50%)]" />
        </div>

        <div className="relative z-10 text-center max-w-4xl mx-auto px-4 py-20">
          <h1 className="text-5xl sm:text-6xl md:text-7xl font-black mb-6 leading-[0.9] tracking-tight">
            <span className="block text-white">GET IN</span>
            <span className="block bg-gradient-to-r from-indigo-400 to-purple-400 bg-clip-text text-transparent">
              TOUCH
            </span>
          </h1>

          <p className="text-lg sm:text-xl text-slate-400 max-w-2xl mx-auto">
            Have a project? Reach out. Available for business anytime.
          </p>
        </div>
      </section>

      {/* Contact Methods */}
      <section className="py-20 px-4">
        <div className="max-w-5xl mx-auto">
          <div className="grid sm:grid-cols-2 gap-6 max-w-2xl mx-auto">
            {contactMethods.map((method, index) => (
              <a
                key={index}
                href={method.link}
                className="group relative overflow-hidden rounded-2xl bg-slate-800/50 border border-slate-700/50 p-6 hover:border-indigo-500/30 transition-all duration-300 flex flex-col"
              >
                <method.icon className="h-7 w-7 text-indigo-400 mb-3" />
                <h3 className="text-xl font-bold text-white mb-2">{method.title}</h3>
                <p className="text-slate-400 flex-1">{method.description}</p>

                <div className="flex items-center text-indigo-400 font-medium group-hover:text-indigo-300 transition-colors mt-4">
                  {method.linkText}
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section id="form" className="py-24 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-indigo-600/20 via-purple-600/20 to-indigo-600/20" />
            <div className="absolute inset-0 bg-slate-900/80" />
            <div className="absolute inset-0 opacity-30"
                 style={{
                   backgroundImage: `radial-gradient(circle at 20% 50%, rgba(99,102,241,0.3) 0%, transparent 50%),
                                     radial-gradient(circle at 80% 50%, rgba(139,92,246,0.3) 0%, transparent 50%)`
                 }} />
            <div className="relative z-10 p-8 sm:p-12">
              <ContactForm showHeading={true} />
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ContactPage;
